<template>
  <div class="w-full h-screen flex flex-col items-center justify-center bg-[#b7cef2] overflow-hidden relative">
    <div class="flex justify-center pt-4">
      <img src="/junkanlogo_transparent_v3.png" class="max-w-sm" />
    </div>
    <div class="relative bg-white rounded-xl shadow py-10 px-6 gap-5 w-[448px] flex flex-col items-center mt-6">
      <div class="text-[#4d586c] text-2xl font-bold leading-[34px] text-center">Forgot Password</div>
      <div class="w-full text-center text-[#3c4452] text-sm leading-snug">
        {{
          isOtpStep
            ? "Enter the OTP we sent to your email"
            : isNewPasswordStep
            ? "Enter the new password"
            : 'Confirm your email and we will send you an OTP'
        }}
      </div>

      <!-- Error Message -->
      <div v-if="error" class="w-full p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded-lg text-sm">
        {{ error }}
      </div>

      <!-- Success Message -->
      <div v-if="success" class="w-full p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm">
        {{ success }}
      </div>

      <div class="w-full flex flex-col justify-center items-center gap-5 mt-5">
        <div v-if="!isOtpStep && !isNewPasswordStep" class="w-full flex flex-col gap-1.5">
          <label class="text-[#4d586c] text-base font-medium">Email</label>
          <input v-model="email" type="email" placeholder="mail@example.com" :class="inputClass(isEmailValid(email))" />
        </div>

        <div v-if="isOtpStep" class="w-full flex flex-col gap-1.5">
          <label class="text-[#4d586c] text-base font-medium">OTP</label>
          <input v-model="otp" type="text" maxlength="4" placeholder="Enter OTP" :class="inputClass(isOtpValid(otp))" />
        </div>

        <div v-if="isNewPasswordStep" class="w-full flex flex-col gap-3">
          <div class="relative w-full">
            <label class="text-slate-600 text-base font-medium">Password</label>
            <input v-model="password" :type="showPassword ? 'text' : 'password'" placeholder="********" :class="inputClass(isPasswordValid(password))" class="pr-10" />
            <button type="button" @click="showPassword = !showPassword" class="absolute right-3 top-2/3 transform -translate-y-1/2 text-blue_primary text-sm">{{ showPassword ? 'Hide' : 'Show' }}</button>
          </div>
          
          <!-- Password Requirements -->
          <div class="text-xs text-gray-600 space-y-1">
            <div class="flex items-center" :class="hasMinLength(password) ? 'text-green-600' : 'text-gray-500'">
              <span class="mr-2">{{ hasMinLength(password) ? '✓' : '○' }}</span>
              At least 8 characters
            </div>
            <div class="flex items-center" :class="hasLowercase(password) ? 'text-green-600' : 'text-gray-500'">
              <span class="mr-2">{{ hasLowercase(password) ? '✓' : '○' }}</span>
              One lowercase letter
            </div>
            <div class="flex items-center" :class="hasUppercase(password) ? 'text-green-600' : 'text-gray-500'">
              <span class="mr-2">{{ hasUppercase(password) ? '✓' : '○' }}</span>
              One uppercase letter
            </div>
            <div class="flex items-center" :class="hasSpecialChar(password) ? 'text-green-600' : 'text-gray-500'">
              <span class="mr-2">{{ hasSpecialChar(password) ? '✓' : '○' }}</span>
              One special character
            </div>
            <div class="flex items-center" :class="passwordsMatch() ? 'text-green-600' : 'text-gray-500'">
              <span class="mr-2">{{ passwordsMatch() ? '✓' : '○' }}</span>
              Passwords match
            </div>
          </div>
          
          <div class="relative w-full">
            <label class="text-slate-600 text-base font-medium">Confirm Password</label>
            <input v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'" placeholder="********" :class="inputClass(confirmPassword === password && !!confirmPassword)" class="pr-10" />
            <button type="button" @click="showConfirmPassword = !showConfirmPassword" class="absolute right-3 top-2/3 transform -translate-y-1/2 text-blue_primary text-sm">{{ showConfirmPassword ? 'Hide' : 'Show' }}</button>
          </div>
        </div>

        <div class="w-full flex flex-col gap-3" v-if="!isNewPasswordStep">
          <button v-if="!isOtpStep" @click="sendOtp" :disabled="!isEmailValid(email) || loading" :class="buttonClass(isEmailValid(email) && !loading)">
            <div class="text-white text-base font-semibold">{{ loading ? 'Sending...' : 'Next' }}</div>
          </button>
          <button v-if="isOtpStep" @click="verifyOtp" :disabled="!isOtpValid(otp) || loading" class="px-3.5 py-[9px] bg-blue_primary rounded-lg shadow justify-center items-center gap-2 flex">
            <div class="text-white text-base font-semibold">{{ loading ? 'Verifying...' : 'Verify OTP' }}</div>
          </button>
          <button @click="cancel" :disabled="loading" class="w-full px-[18px] py-2.5 bg-white rounded-lg shadow border border-[#dfe3f0] text-center">
            <div class="text-[#4d586c] text-base font-semibold">Cancel</div>
          </button>
        </div>

        <div class="w-full" v-else>
          <button @click="newPassword" :disabled="!isPasswordValid(password) || loading" class="px-3.5 py-[9px] bg-blue_primary rounded-lg shadow justify-center items-center gap-2 flex w-full">
            <div class="text-white text-base font-semibold">{{ loading ? 'Updating...' : 'Confirm password' }}</div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

export default {
  setup() {
    const store = useStore();
    const router = useRouter();
    const email = ref("");
    const otp = ref("");
    const isOtpStep = ref(false);
    const isNewPasswordStep = ref(false);
    const password = ref("");
    const confirmPassword = ref("");
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);
    const error = ref("");
    const success = ref("");
    const loading = ref(false);

    const isEmailValid = (x) => /\S+@\S+\.\S+/.test(x);
    const isOtpValid = (x) => x && x.length === 4;
    const isPasswordValid = (pwd) => {
      const minLength = 8;
      const hasUpper = /[A-Z]/.test(pwd);
      const hasLower = /[a-z]/.test(pwd);
      const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
      return pwd.length >= minLength && hasUpper && hasLower && hasSpecial && confirmPassword.value === pwd;
    };

    const hasSpecialChar = (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
    const hasLowercase = (pwd) => /[a-z]/.test(pwd);
    const hasUppercase = (pwd) => /[A-Z]/.test(pwd);
    const hasMinLength = (pwd) => pwd.length >= 8;
    const passwordsMatch = () => confirmPassword.value === password.value && !!confirmPassword.value;

    const inputClass = (ok) => ok ? "w-full px-3 py-2 bg-white rounded-lg shadow border-2 border-green-400" : "w-full px-3 py-2 bg-white rounded-lg shadow border-2 border-blue-400";
    const buttonClass = (ok) => ok ? "px-3.5 py-[9px] bg-blue_primary rounded-lg shadow justify-center items-center gap-2 flex" : "px-3.5 py-[9px] bg-blue-300 rounded-lg shadow justify-center items-center gap-2 flex";

    const sendOtp = async () => {
      loading.value = true;
      error.value = "";
      success.value = "";
      
      try {
        const data = { email: email.value };
        const response = await store.dispatch("forgotPassword", data);
        
        if (response.ok) {
          success.value = "OTP sent to your email successfully!";
          isOtpStep.value = true;
        } else {
          error.value = response.error || "Failed to send OTP. Please try again.";
        }
      } catch (err) {
        error.value = "Network error. Please try again.";
        console.error("Send OTP error:", err);
      } finally {
        loading.value = false;
      }
    };
    const verifyOtp = async () => {
      loading.value = true;
      error.value = "";
      success.value = "";
      
      try {
        const data = { email: email.value, otp: otp.value };
        const resp = await store.dispatch("verifyOtp", data);
        
        if (resp.ok && resp.res) {
          success.value = "OTP verified successfully!";
          isOtpStep.value = false;
          isNewPasswordStep.value = true;
        } else {
          error.value = resp.message || "Invalid OTP. Please try again.";
        }
      } catch (err) {
        error.value = "Network error. Please try again.";
        console.error("Verify OTP error:", err);
      } finally {
        loading.value = false;
      }
    };
    const newPassword = async () => {
      loading.value = true;
      error.value = "";
      success.value = "";
      
      try {
        const data = { email: email.value, password: password.value };
        const response = await store.dispatch("setNewPassword", data);
        
        if (response.ok) {
          success.value = "Password updated successfully! Redirecting to login...";
          setTimeout(() => {
            router.push({ name: "Login" });
          }, 2000);
        } else {
          error.value = response.error || "Failed to update password. Please try again.";
        }
      } catch (err) {
        error.value = "Network error. Please try again.";
        console.error("Set new password error:", err);
      } finally {
        loading.value = false;
      }
    };
    const cancel = () => { email.value = ""; otp.value = ""; router.push({ name: "Login" }); };

    return { email, otp, isOtpStep, isNewPasswordStep, password, confirmPassword, showPassword, showConfirmPassword, error, success, loading, isEmailValid, isOtpValid, isPasswordValid, hasSpecialChar, hasLowercase, hasUppercase, hasMinLength, passwordsMatch, inputClass, buttonClass, sendOtp, verifyOtp, newPassword, cancel };
  },
};
</script>


